import {getRootStore} from '../../app.bootstrap';
import {MeetingMode} from '@techsee/techsee-common/lib/constants/room.constants';
import {RouteName, RouteNameType} from '../../router/route-names';
import {locales} from '@techsee/techsee-common/lib/helpers/i18n.settings';
import {PromiseUtilsService} from '@techsee/techsee-client-services/lib/services/PromiseUtilsService';
import {getAppTracer} from '../../../app.tracer';
import get from 'lodash/get';
import {UrlUtilsService} from '@techsee/techsee-client-services/lib/services/UrlUtilsService';

declare const BASE_PATH: string;

export const DASHBOARD_LANGUAGE_KEY = 'techseeDashboardLang';

export const SupportedLanguagesEnum = Object.freeze({
    English: 'en_US',
    Dutch: 'nl_NL',
    French: 'fr_FR',
    German: 'de_DE',
    Greek: 'el_GR',
    Hebrew: 'he_IL',
    Arabic: 'ar_AR',
    Italian: 'it_IT',
    Japanese: 'jp_JP',
    Korean: 'ko_KR',
    Polish: 'pl_PL',
    Portuguese: 'pt_PT',
    Russian: 'ru_RU',
    Spanish: 'es_ES',
    Swedish: 'sv_SE',
    Turkish: 'tr_TR',
    Slovak: 'sk_SK',
    Lithuanian: 'lt_LT',
    Danish: 'da_DK',
    Norwegian: 'nv_NO',
    Finnish: 'fi_FI',
    Ukrainian: 'uk_UA',
    Indonesian: 'id_ID',
    Vietnamese: 'vi_VN',
    Chinese: 'zh_CN'
});

const trace = getAppTracer('general.helper');

type CurrentUserParams = {
    alias?: string;
    returnURL?: string;
    userAgent?: string;
    customerId?: string;
    customerNumber?: string;
    customerCountryCode?: string;
    initialState?: string;
    roomId?: string;
    guid?: string;
    roomCode?: string;
    audio?: string;
    subDomain?: string;
    sw?: string;
    prg?: string;
    observe?: string;
    communityId?: string;
    startDate?: string;
    endDate?: string;
};

function tryLoginWithOTT(
    tsUrlUtils: any,
    findCurrentUser: any,
    getAuthToken: any,
    clearAuthToken: any,
    loginWithOneTimeToken: any,
    ott: string,
    currentUserParams: CurrentUserParams
): Promise<any> {
    let wasRedeemed = false;

    return loginWithOneTimeToken(ott)
        .then((res: {isRedeemed: boolean}) => {
            wasRedeemed = res.isRedeemed;

            if (wasRedeemed) {
                if (getAuthToken()) {
                    return {redirect: false, wasRedeemed: true};
                }

                if (tsUrlUtils.getParamValue('emb')) {
                    // OTT failure with embedded dashboard should not be blocked
                    return {
                        embeddedDashboardFailure: true,
                        embeddedDashboardFailureError: 'Auth with OTT was not successful'
                    };
                }
            }

            return findCurrentUser('current', {
                params: currentUserParams,
                bypassCache: true
            }).then((res: any) => {
                if (!res) {
                    throw new Error('User Not Found');
                } else return res;
            });
        })
        .catch((err: any) => {
            // Might have been using stale token from local storage
            if (!wasRedeemed && getAuthToken()) {
                console.warn('An invalid pre-existing token found, trying again with OTT', err);

                clearAuthToken();

                return tryLoginWithOTT(
                    tsUrlUtils,
                    findCurrentUser,
                    getAuthToken,
                    clearAuthToken,
                    loginWithOneTimeToken,
                    ott,
                    currentUserParams
                );
            }

            if (tsUrlUtils.getParamValue('emb')) {
                console.error(err);

                // OTT failure with embedded dashboard should not be blocked
                return {
                    embeddedDashboardFailure: true,
                    embeddedDashboardFailureError: err
                };
            }

            throw err;
        });
}

export function goToDashboard(emb: boolean, sw: MeetingMode): RouteNameType {
    if (sw === MeetingMode.faceMeet.toLowerCase()) {
        return RouteName.MULTIPARTY_DASHBOARD;
    }

    return emb ? RouteName.DASHBOARD_MAIN : RouteName.DASHBOARD_ENTRY;
}

export function initClientLanguage(): string {
    const browserUtilsService = getRootStore().browserUtilsService;
    const currentLanguage = browserUtilsService.getFromLocalStorage(DASHBOARD_LANGUAGE_KEY);
    const foundLocales = locales.filter((locale) => locale.value === currentLanguage);

    return foundLocales && foundLocales.length === 1 ? foundLocales[0].name : 'English';
}

export function getDashboardLanguage(currentAccount: any, roomData?: any) {
    const browserUtilsService = getRootStore().browserUtilsService;
    const language = get(roomData, 'agentLanguage');

    if (language) {
        return language;
    }

    if (currentAccount?.protectedSettings?.allowAgentToChangeHisLanguage) {
        const localStorageLang = browserUtilsService.getFromLocalStorage(DASHBOARD_LANGUAGE_KEY);

        return localStorageLang ? localStorageLang : 'en_US';
    }

    return currentAccount?.settings?.language || 'en_US';
}

export function setDashboardLanguage(agentLanguage: string) {
    const browserUtilsService = getRootStore().browserUtilsService;

    browserUtilsService.saveToLocalStorage(DASHBOARD_LANGUAGE_KEY, agentLanguage);
}

export function openLinkInNewTab(url: string) {
    if (!url) {
        return;
    }

    window.open(url);
}

export function handleOAuthCallback(code: string, error: string, $state: any) {
    const scope = UrlUtilsService.extractQueryParamValueFromUrl(window.location.search, 'scope');
    const state = UrlUtilsService.extractQueryParamValueFromUrl(window.location.search, 'state');
    const errorDescription = UrlUtilsService.extractQueryParamValueFromUrl(window.location.search, 'error_subtype');

    const payload: any = {state};

    if (scope) {
        payload.scope = scope;
    }

    if (code) {
        payload.code = code;
    }

    if (error) {
        payload.error = error;
    }

    if (errorDescription) {
        payload.errorDescription = errorDescription;
    }
    const ssoApiService = getRootStore().ssoApiService;
    return ssoApiService
        .oAuthLoginCallback(payload)
        .then((res: any) => {
            if (!res.redirectUrl) {
                throw new Error('No redirect URL');
            }

            window.location.href = res.redirectUrl;
        })
        .catch((err: any) => {
            const traceMessage = `Failed to redirect from OAuth callback: ${err}`;
            const tracePromise = () => trace.error(traceMessage, err);
            const tracePromiseTimeout = 3000;

            return PromiseUtilsService.startPromiseWithTimeout(tracePromise, tracePromiseTimeout)
                .catch((err) => console.error(traceMessage + ' >> trace failed: ' + err))
                .then(() => $state.go('login'));
        });
}

export const LARGE_SCREEN = 1280;
